import type {
    LoginBodyDto,
    LoginDto,
} from "@authentication/data/dto/login.dto";
import {
    LoginBlockedDtoSchema,
    LoginDtoSchema,
} from "@authentication/data/dto/login.dto";
import type {
    AuthenticatedUser,
    AuthenticatedUserInputProps,
} from "@authentication/domain/models/authenticated-user.model";
import { AuthenticatedUserSchema } from "@authentication/domain/models/authenticated-user.model";
import type {
    Login,
    LoginInputProps,
    LoginUser,
    LoginUserInputProps,
    PasswordRecovery,
    PasswordRecoveryInputProps,
} from "@authentication/domain/models/login-user.model";
import {
    LoginSchema,
    LoginUserSchema,
    PasswordRecoverySchema,
} from "@authentication/domain/models/login-user.model";
import type { Nullable } from "@iot-platform/core";
import { UserRoleEnumMapper } from "@iot-platform/user";
import { inject, injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import * as z from "zod";

// Local types to avoid presentation layer dependencies
export interface LoginFormValues {
    email: string;
    password: string;
}

export interface PasswordRecoveryFormValues {
    email: string;
}

@injectable()
export class LoginUserMapper {
    constructor(
        @inject(UserRoleEnumMapper)
        private readonly userRoleEnumMapper: UserRoleEnumMapper,
    ) {}
    map(authenticatedUserDto: LoginDto, token: string): Nullable<LoginUser> {
        const authenticatedUserDtoParsed =
            LoginDtoSchema.safeParse(authenticatedUserDto);

        if (!authenticatedUserDtoParsed.success) {
            console.error(authenticatedUserDtoParsed.error.issues);
            return null;
        }

        const userRole = this.userRoleEnumMapper.map(
            authenticatedUserDto.user.roles[0],
        );

        const authenticatedUserResult = LoginUserSchema.safeParse({
            id: authenticatedUserDto.user.id,
            rol: userRole ?? null,
            email: authenticatedUserDto.user.email,
            name: authenticatedUserDto.user.username ?? null,
            token: token,
            timeZone: authenticatedUserDto.user.time_zone,
        } satisfies LoginUserInputProps);

        return authenticatedUserResult.success
            ? authenticatedUserResult.data
            : null;
    }

    mapToLogin(login: LoginFormValues): Result<Login, z.ZodError<Login>> {
        const maybeLogin = LoginSchema.safeParse({
            email: login.email,
            password: login.password,
        } satisfies LoginInputProps);

        return maybeLogin.success ? ok(maybeLogin.data) : err(maybeLogin.error);
    }

    mapToPasswordRecovery(
        login: PasswordRecoveryFormValues,
    ): Result<PasswordRecovery, z.ZodError<PasswordRecovery>> {
        const maybePasswordRecovery = PasswordRecoverySchema.safeParse({
            email: login.email,
        } satisfies PasswordRecoveryInputProps);

        return maybePasswordRecovery.success
            ? ok(maybePasswordRecovery.data)
            : err(maybePasswordRecovery.error);
    }

    /**
     * Extracts the unlock instant from a 429 login response body. Returns
     * null when the body is missing or `blocked_until` is not a valid
     * ISO-8601 datetime, so the caller can fall back to a generic message.
     */
    mapBlockedUntil(data: unknown): Nullable<Date> {
        const parsed = LoginBlockedDtoSchema.safeParse(data);

        if (!parsed.success) return null;

        return new Date(parsed.data.blocked_until);
    }

    mapToBodyDto(email: string, password: string): LoginBodyDto {
        return {
            user: {
                login: email,
                password: password,
            },
        };
    }

    mapUser(loginUser: LoginUser): Nullable<AuthenticatedUser> {
        const authenticatedUserResult = AuthenticatedUserSchema.safeParse({
            id: loginUser.id,
            rol: loginUser.rol,
            name: loginUser.name ?? null,
            email: loginUser.email,
            language: loginUser.language ?? undefined,
            timeZone: loginUser.timeZone,
        } satisfies AuthenticatedUserInputProps);

        return authenticatedUserResult.success
            ? authenticatedUserResult.data
            : null;
    }
}
